import { apiClient, handleApiRequest } from './client'

// ===========================================
// Types (data-fix batch)
// ===========================================

export type DataQualityCategory = 'subject' | 'room' | 'klass'
export type TokenCleanupCategory = DataQualityCategory | 'ignore'

export interface TeacherRef {
  id: string
  name: string
  code: string | null
  entry_count?: number
  manually_confirmed?: boolean
}

export interface DataQualitySuggestion {
  target_id: string | null
  // null for ambiguous_canonical (multiple canonical candidates)
  target_name: string | null
  confidence: number
  strategy: string
}

export interface DataQualitySplit {
  suspicious: boolean
  klass?: string
  room?: string
  reason?: string
}

export interface DataQualityItem {
  category: DataQualityCategory
  value: string | null
  is_null_value: boolean
  entry_count: number
  teacher_count: number
  sample_teachers: (string | null)[]
  teachers: TeacherRef[]
  sample_entry_ids: string[]
  cross_fields: string[]
  suggestion: DataQualitySuggestion | null
  split: DataQualitySplit | null
}

export interface DataQualityStats {
  total_groups: number
  total_unmapped_entries: number
  teachers_affected: number
  safe_suggestions: number
}

export interface DataQualityResponse {
  items: DataQualityItem[]
  total: number
  skip: number
  limit: number
  stats: DataQualityStats
}

export interface AssignPayload {
  category: DataQualityCategory
  value: string
  target_id?: string
  target_value?: string
}

export interface ReassignPayload {
  category: DataQualityCategory
  value: string
  target_category: DataQualityCategory
  target_value?: string
  target_id?: string
}

export interface BulkApplyItemPayload {
  action: 'assign' | 'reassign' | 'split' | 'change_value'
  category?: DataQualityCategory
  value: string
  target_id?: string
  target_value?: string
  target_category?: DataQualityCategory
  klass?: string
  room?: string
  new_value?: string
  teacher_ids?: string[]
  allow_create_subject?: boolean
}

export interface BulkApplyResponse {
  batch_id: string
  affected_rows: number
  created_records: number
  errors: { item: unknown; error: string }[]
}

export interface PreviewResponse {
  items: {
    category: string
    value: string
    action: string
    affected_rows: number
    affected_teachers: number
    target_category?: string
  }[]
  created_records: { table: string; name: string }[]
  conflicts: { item: unknown; reason: string }[]
  total_affected_rows: number
}

export interface BatchInfo {
  batch_id: string
  created_at: string | null
  affected_entries_count: number
  status: string
  is_reverted: boolean
  can_undo: boolean
}

export type CodeConceptKind = 'class' | 'room'

export interface ConceptPiece {
  start: number
  end: number
  text: string
  label?: string | null
}

export interface CodeConcept {
  id: string
  example_text: string
  pieces: ConceptPiece[]
  created_at: string | null
}

// ===========================================
// dq-v2: teacher context + canonical values
// ===========================================

export interface TeacherContextEntry {
  id: string
  date: string | null
  day_of_week: number | null
  time_slot_id?: string | null
  time_slot_name: string | null
  start_time: string | null
  end_time: string | null
  subject_name: string | null
  room_name: string | null
  klass_name: string | null
  subject_id?: string | null
  room_id?: string | null
  klass_id?: string | null
  subject_ids?: string[] | null
  lesson_number: number | null
  is_recurring: boolean
  valid_from: string | null
  valid_to: string | null
  substitution_excluded?: boolean
  ampel?: 'red' | 'yellow' | 'green' | 'unknown' | null
  ampel_reasons?: string[]
  pdf_raw_text?: string | null
}

export interface TeacherContextPdf {
  file_id: string
  original_filename: string | null
  page: number | null
  source: 'bulk_mapping' | 'fallback'
}

export interface TeacherContextResponse {
  teacher: TeacherRef
  entries: TeacherContextEntry[]
  slots?: Array<{ id: string; slot_number: number | null; start_time: string | null; end_time: string | null; is_break?: boolean }>
  pdf: TeacherContextPdf | null
}

export interface CanonicalValue {
  id: string
  name: string
}

export interface PdfPageResponse {
  pdf_base64: string
  page_number: number
  total_pages: number
}

export type TokenCleanupGroup = 'fach' | 'klass' | 'raum' | 'ignore' | 'unused' | 'ok'
export type TokenCleanupSuggestion = DataQualityCategory | 'ignore'

export interface TokenCleanupAliasOf {
  category: DataQualityCategory
  id: string
  name: string
}

export interface TokenCleanupSplitPart {
  token: string
  field: string
}

export interface TokenCleanupItem {
  id: string
  name: string
  category: TokenCleanupCategory
  entry_count: number
  teacher_count: number
  teachers: TeacherRef[]
  group: TokenCleanupGroup
  suggestion: TokenCleanupSuggestion
  alias_of?: TokenCleanupAliasOf | null
  split_parts?: TokenCleanupSplitPart[] | null
  auto_split?: boolean | null
  was_split?: boolean | null
  split_of?: string | null
}

export interface TokenCleanupListResponse {
  items: TokenCleanupItem[]
  total: number
}

export interface TokenCleanupApplyResponse {
  batch_id: string | null
  affected_rows: number
  skipped_conflicts: number
  deactivated: boolean
  target_category: string | null
  target_name: string | null
}

export type KenntnisGroup = 'ignore' | 'subject' | 'room' | 'class_name' | 'split'

export interface KenntnisDeltaItem {
  token: string
  source?: string | null
  field?: string | null
  parts?: { token: string; field?: string }[] | null
}

export interface KenntnisPreviewResponse {
  live: {
    ignore: number
    mapped: number
    split: number
    patterns_live: number
  }
  delta: Record<KenntnisGroup, KenntnisDeltaItem[]>
  already_live: { aliases: number; patterns: number }
  skipped_year_classes: string[]
}

export interface KenntnisSealResponse {
  written: number
  preview: KenntnisPreviewResponse
}

export function pdfBase64ToObjectUrl(base64: string): string {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
}

// ===========================================
// API
// ===========================================

export const timetableDataQualityApi = {
  getDataQuality: (params: { category?: string; search?: string; skip?: number; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params.category && params.category !== 'all') qs.set('category', params.category)
    if (params.search) qs.set('search', params.search)
    qs.set('skip', String(params.skip ?? 0))
    qs.set('limit', String(params.limit ?? 50))
    return handleApiRequest<DataQualityResponse>(
      apiClient.get(`/api/v1/admin/timetable/data-quality?${qs.toString()}`)
    )
  },

  assign: (data: AssignPayload) =>
    handleApiRequest<{ affected_rows: number }>(
      apiClient.post('/api/v1/admin/timetable/data-quality/assign', data)
    ),

  reassign: (data: ReassignPayload) =>
    handleApiRequest<{ affected_rows: number }>(
      apiClient.post('/api/v1/admin/timetable/data-quality/reassign', data)
    ),

  split: (data: { value: string; klass?: string; room?: string }) =>
    handleApiRequest<{ affected_rows: number }>(
      apiClient.post('/api/v1/admin/timetable/data-quality/split', data)
    ),

  preview: (data: { items: BulkApplyItemPayload[]; include_safe_suggestions?: boolean }) =>
    handleApiRequest<PreviewResponse>(
      apiClient.post('/api/v1/admin/timetable/data-quality/preview', data)
    ),

  bulkApply: (data: { items: BulkApplyItemPayload[]; include_safe_suggestions?: boolean }) =>
    handleApiRequest<BulkApplyResponse>(
      apiClient.post('/api/v1/admin/timetable/data-quality/bulk-apply', data)
    ),

  bulkUndo: (batchId: string) =>
    handleApiRequest<{
      restored_rows: number
      removed_created_records: number
      skipped_still_referenced_records?: number
    }>(
      apiClient.post(`/api/v1/admin/timetable/data-quality/bulk-undo/${batchId}`)
    ),

  listBatches: (limit = 20) =>
    handleApiRequest<{ batches: BatchInfo[] }>(
      apiClient.get(`/api/v1/admin/timetable/data-quality/batches?limit=${limit}`)
    ),

  listConcepts: (kind: CodeConceptKind) =>
    handleApiRequest<{ concepts: CodeConcept[] }>(
      apiClient.get(`/api/v1/admin/timetable/${kind}-code-concepts`)
    ),

  createConcept: (kind: CodeConceptKind, example_text: string, pieces: ConceptPiece[]) =>
    handleApiRequest<CodeConcept>(
      apiClient.post(`/api/v1/admin/timetable/${kind}-code-concepts`, {
        example_text,
        pieces,
        previewed: true,
      })
    ),

  decomposeConcept: (kind: CodeConceptKind, example_text: string) =>
    handleApiRequest<{ example_text: string; pieces: ConceptPiece[] }>(
      apiClient.post(`/api/v1/admin/timetable/${kind}-code-concepts/decompose`, { example_text })
    ),

  previewConcept: (kind: CodeConceptKind, example_text: string, pieces: ConceptPiece[]) =>
    handleApiRequest<{ tokens: string[]; empty_import: boolean; equivalent_example?: string | null }>(
      apiClient.post(`/api/v1/admin/timetable/${kind}-code-concepts/preview`, { example_text, pieces })
    ),

  deleteConcept: (kind: CodeConceptKind, id: string) =>
    handleApiRequest<{ deleted: boolean }>(
      apiClient.delete(`/api/v1/admin/timetable/${kind}-code-concepts/${id}`)
    ),

  wipeConcepts: () =>
    handleApiRequest<{ deleted: number }>(
      apiClient.post('/api/v1/admin/timetable/code-concepts/wipe', { confirm: true })
    ),

  wipeMemory: () =>
    handleApiRequest<{ deleted: number }>(
      apiClient.post('/api/v1/admin/timetable/token-memory/wipe', { confirm: true })
    ),

  wipeCatalog: () =>
    handleApiRequest<{ deleted: number }>(
      apiClient.post('/api/v1/admin/timetable/catalog/wipe', { confirm: true })
    ),

  // dq-v2 B2: teacher context (system timetable + pdf mapping)
  setTeacherReview: (data: {
    category: DataQualityCategory | 'timetable'
    value: string | null
    is_null_value: boolean
    teacher_id: string
    confirmed: boolean
  }) =>
    handleApiRequest<{
      teacher_id: string
      category: DataQualityCategory | 'timetable'
      value: string | null
      is_null_value: boolean
      manually_confirmed: boolean
    }>(apiClient.post('/api/v1/admin/timetable/data-quality/teacher-review', data)),

  extendDouble: (data: { entry_id: string; direction: 'up' | 'down' }) =>
    handleApiRequest<{
      entry_id: string
      neighbor_id: string
      direction: 'up' | 'down'
      created: boolean
      pdf_span_start: number | null
      pdf_span_end: number | null
      undo_id: string | null
    }>(apiClient.post('/api/v1/admin/timetable/data-quality/extend-double', data)),

  deleteEntry: (entryId: string) =>
    handleApiRequest<{ entry_id: string; deleted: boolean }>(
      apiClient.post('/api/v1/admin/timetable/data-quality/delete-entry', { entry_id: entryId }),
    ),

  restoreEntry: (entryId: string) =>
    handleApiRequest<{ entry_id: string; deleted: boolean }>(
      apiClient.post('/api/v1/admin/timetable/data-quality/restore-entry', { entry_id: entryId }),
    ),

  undoExtend: (data: { undo_id: string }) =>
    handleApiRequest<{ undo_id: string; restored: number; deleted_id: string | null }>(
      apiClient.post('/api/v1/admin/timetable/data-quality/undo-extend', data),
    ),

  getTeacherContext: (teacherId: string, batchId?: string, token?: string) => {
    const qs = new URLSearchParams()
    if (batchId) qs.set('batch_id', batchId)
    if (token) qs.set('token', token)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<TeacherContextResponse>(
      apiClient.get(`/api/v1/admin/timetable/data-quality/teacher-context/${teacherId}${suffix}`)
    )
  },

  resolveFinding: (data: {
    entry_id: string
    action: 'assign_subject' | 'confirm_empty' | 'split_concat'
    target_id?: string
    target_value?: string
    allow_create_subject?: boolean
    apply_same_holes?: boolean
    same_holes_scope?: 'teacher' | 'school'
    dry_run?: boolean
  }) =>
    handleApiRequest<{
      batch_id: string | null
      affected_rows: number
      affected_teachers: string[]
      created_records: number
      errors: { item?: unknown; error: string }[]
    }>(apiClient.post('/api/v1/admin/timetable/data-quality/resolve-finding', data)),

  getCanonicalValues: (category: DataQualityCategory) =>
    handleApiRequest<{ values: CanonicalValue[] }>(
      apiClient.get(
        `/api/v1/admin/timetable/data-quality/canonical-values?category=${category}`
      )
    ),

  getFindings: (batchId?: string) => {
    const qs = new URLSearchParams()
    if (batchId) qs.set('batch_id', batchId)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<{
      batch_id: string | null
      heuristic_banner: boolean
      import_file_id: string | null
      import_batch_id: string | null
      import_available: boolean
      teachers: Array<{
        teacher_id: string
        code: string | null
        name: string | null
        first_name?: string | null
        last_name?: string | null
        red: number
        yellow: number
        green: number
        manually_confirmed?: boolean
        findings: Array<{
          entry_id: string
          day_of_week: number | null
          lesson_number: number | null
          color: string
          reasons: string[]
          subject_name: string | null
          klass_name: string | null
          room_name: string | null
          pdf_raw_text: string | null
          pdf_had_subject: boolean | null
          pdf_span_start: number | null
          pdf_span_end: number | null
          suggested_action: 'assign_subject' | 'confirm_empty' | 'split_concat' | 'reimport' | null
          hole_key_usable: boolean
          same_holes_teacher: number
          same_holes_school: number
        }>
      }>
    }>(apiClient.get(`/api/v1/admin/timetable/data-quality/findings${suffix}`))
  },

  getPdfPage: (fileId: string, pageNumber: number) =>
    handleApiRequest<PdfPageResponse>(
      apiClient.get('/api/v1/admin/timetable/data-quality/pdf-page', {
        params: { file_id: fileId, page_number: pageNumber },
        timeout: 120000,
      })
    ),

  getTokens: (params: { category: TokenCleanupCategory; search?: string }) => {
    const qs = new URLSearchParams()
    qs.set('category', params.category)
    if (params.search) qs.set('search', params.search)
    return handleApiRequest<TokenCleanupListResponse>(
      apiClient.get(`/api/v1/admin/timetable/data-quality/tokens?${qs.toString()}`)
    )
  },

  applyToken: (data: {
    category: TokenCleanupCategory
    catalog_id?: string
    catalog_ids?: string[]
      action: 'recategorize' | 'ignore' | 'confirm' | 'delete' | 'split'
    target_category?: DataQualityCategory
    parts?: { token: string; field: string }[]
    canonical_id?: string
  }) =>
    handleApiRequest<TokenCleanupApplyResponse>(
      apiClient.post('/api/v1/admin/timetable/data-quality/tokens/apply', data)
    ),

  getKenntnisPreview: () =>
    handleApiRequest<KenntnisPreviewResponse>(
      apiClient.get('/api/v1/admin/timetable/data-quality/knowledge/preview')
    ),

  sealKenntnis: (groups?: KenntnisGroup[]) =>
    handleApiRequest<KenntnisSealResponse>(
      apiClient.post('/api/v1/admin/timetable/data-quality/knowledge/seal', { groups })
    ),
}
